// src/components/common/EmptyState.tsx
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
} from '@mui/material';
import {
  InboxOutlined as DefaultEmptyIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';

export interface EmptyStateProps {
  /** Mensaje principal a mostrar */
  message?: string;
  /** Descripción adicional opcional */
  description?: string;
  /** Icono personalizado */
  icon?: React.ReactNode;
  /** Texto del botón de acción */
  actionText?: string;
  /** Icono del botón de acción (por defecto, uno de "añadir"; un estado
   *  vacío por un filtro activo debería pasar uno de "limpiar" en su lugar). */
  actionIcon?: React.ReactNode;
  /** Función a ejecutar cuando se hace clic en el botón */
  onAction?: () => void;
  /** Mostrar el botón de acción */
  showAction?: boolean;
  /** Altura mínima del contenedor */
  minHeight?: number | string;
}

/**
 * EmptyState - Componente reutilizable para mostrar estados vacíos
 * 
 * Características:
 * - Mensaje e icono personalizables
 * - Botón de acción opcional
 * - Diseño consistente en toda la aplicación
 * - Responsive design
 * - Soporte completo para traducciones
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  message,
  description,
  icon,
  actionText,
  actionIcon,
  onAction,
  showAction = false,
  minHeight = 300,
}) => {
  const translate = useTranslate();
  const theme = useTheme();

  const defaultMessage = translate('ra.page.empty', 'No data found');
  const defaultActionText = translate('ra.action.create', 'Create');

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight,
        p: 4,
        textAlign: 'center',
      }}
    >
      {/* Icono */}
      <Box
        sx={{
          mb: 2,
          color: theme.palette.grey[400],
          '& > svg': {
            fontSize: '4rem',
          },
        }}
      >
        {icon || <DefaultEmptyIcon />}
      </Box>

      {/* Mensaje principal */}
      <Typography
        variant="h6"
        color="text.secondary"
        gutterBottom
        sx={{ mb: 1 }}
      >
        {message || defaultMessage}
      </Typography>

      {/* Descripción */}
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, maxWidth: 400 }}
        >
          {description}
        </Typography>
      )}

      {/* Botón de acción */}
      {showAction && onAction && (
        <Button
          variant="contained"
          color="primary"
          startIcon={actionIcon ?? <AddIcon />}
          onClick={onAction}
          sx={{ mt: 2 }}
        >
          {actionText || defaultActionText}
        </Button>
      )}
    </Paper>
  );
};

export default EmptyState;